import { createAsyncThunk } from "@reduxjs/toolkit";
import { createContractLoginApi } from "../../../api";
import type {
  ProblemDetails,
  TokenViewModel,
} from "../../../api/axcloud";
import { persistAccessToken } from "../services/tokenStorage";
import { completeTwoFactorLogin } from "./helpers";
import type { LoginThunkConfig } from "./types";

interface TwoFactorLoginArgs {
  authenticationCode: string;
}

interface RecoveryCodeLoginArgs {
  recoveryCode: string;
}

const expiredTwoFactorProblem: ProblemDetails = {
  detail: "二要素認証の有効期限が切れています。もう一度ログインしてください。",
};

const cancelledTwoFactorProblem: ProblemDetails = {
  detail: "二要素認証がキャンセルされました。",
};

/**
 * 二要素認証のコードでログインします。
 */
export const contractLogin2fa = createAsyncThunk<
  TokenViewModel,
  TwoFactorLoginArgs,
  LoginThunkConfig
>(
  "login/contractLogin2fa",
  async (arg, thunk) => {
    const accessToken = thunk.getState().login.pendingAccessToken;
    if (!accessToken) {
      return thunk.rejectWithValue(expiredTwoFactorProblem);
    }

    const result = await completeTwoFactorLogin(
      thunk,
      accessToken,
      () =>
        createContractLoginApi(accessToken).contractLogin2faAsync({
          authenticationCode: arg.authenticationCode,
        }),
      cancelledTwoFactorProblem
    );
    if (!result.ok) {
      return thunk.rejectWithValue(result.problem);
    }

    persistAccessToken(result.token);
    return result.token;
  }
);

/**
 * 二要素認証のリカバリーコードでログインします。
 */
export const contractLoginRecoveryCode = createAsyncThunk<
  TokenViewModel,
  RecoveryCodeLoginArgs,
  LoginThunkConfig
>(
  "login/contractLoginRecoveryCode",
  async (arg, thunk) => {
    const accessToken = thunk.getState().login.pendingAccessToken;
    if (!accessToken) {
      return thunk.rejectWithValue(expiredTwoFactorProblem);
    }

    const result = await completeTwoFactorLogin(
      thunk,
      accessToken,
      () =>
        createContractLoginApi(accessToken).contractLoginRecoveryCodeAsync({
          recoveryCode: arg.recoveryCode,
        }),
      cancelledTwoFactorProblem
    );
    if (!result.ok) {
      return thunk.rejectWithValue(result.problem);
    }

    persistAccessToken(result.token);
    return result.token;
  }
);